import React, { useMemo, useState } from 'react';
import { ShoppingCart, Search, Car, ExternalLink, Tag, Plus, Trash2, Copy, Info } from 'lucide-react';
import { toast } from 'sonner';
import PageHeader from '../components/PageHeader';
import { useData } from '../context/DataContext';
import { useT } from '../context/LanguageContext';
import { confirmDialog } from '../lib/confirm';

const enc = encodeURIComponent;

// Per-shop deep-link builder. Waar de shop een betrouwbare zoekterm-URL heeft gebruiken we die
// direct; anders een op de shop gerichte Google-zoekopdracht (landt in de webshop).
const SHOPS = [
  { id: 'autodoc', naam: 'Autodoc', kleur: '#16a34a', domein: 'autodoc.nl',
    build: (q) => `https://www.autodoc.nl/search?keyword=${enc(q)}` },
  { id: 'mijnautoonderdelen', naam: 'MijnAutoOnderdelen', kleur: '#2563eb', domein: 'mijnautoonderdelen.nl',
    build: (q) => `https://www.mijnautoonderdelen.nl/catalogsearch/result/?q=${enc(q)}` },
  { id: 'winparts', naam: 'Winparts', kleur: '#ea580c', domein: 'winparts.nl',
    build: (q) => `https://www.google.com/search?q=${enc(q + ' site:winparts.nl')}` },
  { id: 'misterauto', naam: 'Mister Auto', kleur: '#dc2626', domein: 'misterauto.nl',
    build: (q) => `https://www.google.com/search?q=${enc(q + ' site:misterauto.nl')}` },
];

const SNELKEUZE = ['Remblokken', 'Remschijven', 'Oliefilter', 'Luchtfilter', 'Bougies', 'Accu', 'Distributieriem', 'Schokdempers', 'Koppeling', 'Ruitenwissers'];

export default function Prijsvergelijk() {
  const { t } = useT();
  const { klantAutos, gegevens, updateGegevens, rdwLookup } = useData();
  const [voertuig, setVoertuig] = useState('');      // kenteken-keuze uit dropdown
  const [kenteken, setKenteken] = useState('');      // handmatig kenteken
  const [info, setInfo] = useState(null);            // {merk, model, bouwjaar}
  const [onderdeel, setOnderdeel] = useState('');
  const [rdwBusy, setRdwBusy] = useState(false);

  const coupons = gegevens?.webshopCoupons || [];

  // Bepaal voertuig-context (merk/model/bouwjaar) uit dropdown-keuze of handmatige RDW
  const gekozenAuto = useMemo(() => klantAutos.find(a => a.kenteken === voertuig), [klantAutos, voertuig]);
  const context = info || (gekozenAuto ? { merk: gekozenAuto.merk, model: gekozenAuto.model, bouwjaar: gekozenAuto.bouwjaar } : null);
  const actiefKenteken = voertuig || kenteken;

  const query = useMemo(() => {
    const delen = [onderdeel.trim()];
    if (context) { delen.push(context.merk, context.model, context.bouwjaar); }
    return delen.filter(Boolean).join(' ').trim();
  }, [onderdeel, context]);

  const doRdw = async () => {
    const kt = kenteken.trim();
    if (!kt) { toast.error(t('Vul een kenteken in', 'Vul een kenteken in')); return; }
    setRdwBusy(true);
    try {
      const r = await rdwLookup(kt);
      setInfo({ merk: r.merk || '', model: r.handelsbenaming || '', bouwjaar: (r.datumEersteToelating || '').slice(0, 4) });
      setVoertuig('');
      toast.success(`${r.merk || ''} ${r.handelsbenaming || ''}`.trim() || t('Voertuig gevonden', 'Voertuig gevonden'));
    } catch (e) {
      toast.error(t('Kenteken niet gevonden', 'Kenteken niet gevonden'), { description: e.response?.data?.detail || e.message });
    } finally { setRdwBusy(false); }
  };

  const openAlle = () => {
    if (!onderdeel.trim()) { toast.error(t('Typ eerst een onderdeel', 'Typ eerst een onderdeel')); return; }
    SHOPS.forEach((s, i) => setTimeout(() => window.open(s.build(query), '_blank', 'noopener'), i * 120));
  };

  // Kortingscodes beheren (opgeslagen in gegevens.webshopCoupons)
  const [nieuw, setNieuw] = useState({ shop: SHOPS[0].id, code: '', omschrijving: '' });
  const saveCoupons = (list) => updateGegevens({ ...gegevens, webshopCoupons: list });
  const addCoupon = () => {
    if (!nieuw.code.trim()) { toast.error(t('Vul een code in', 'Vul een code in')); return; }
    const list = [...coupons, { id: `c${Date.now()}`, shop: nieuw.shop, code: nieuw.code.trim().toUpperCase(), omschrijving: nieuw.omschrijving.trim() }];
    saveCoupons(list);
    setNieuw({ shop: nieuw.shop, code: '', omschrijving: '' });
    toast.success(t('Kortingscode opgeslagen', 'Kortingscode opgeslagen'));
  };
  const delCoupon = async (id) => {
    if (!(await confirmDialog(t('Kortingscode verwijderen?', 'Kortingscode verwijderen?')))) return;
    saveCoupons(coupons.filter(c => c.id !== id));
    toast.success(t('Verwijderd', 'Verwijderd'));
  };
  const copyCode = (code) => { navigator.clipboard?.writeText(code); toast.success(`${code} ${t('gekopieerd', 'gekopieerd')}`); };

  const shopById = (id) => SHOPS.find(s => s.id === id) || SHOPS[0];

  return (
    <div className="space-y-6" data-testid="prijsvergelijk-page">
      <PageHeader pageKey="prijsvergelijk" title={t('Prijsvergelijk onderdelen', 'Prijsvergelijk onderdelen')}
        subtitle={t('Kies een voertuig, typ het onderdeel en vergelijk de prijzen in 4 webshops', 'Kies een voertuig, typ het onderdeel en vergelijk de prijzen in 4 webshops')} />

      {/* Zoekblok */}
      <div className="cs-card" data-testid="pv-zoekblok" style={{ padding: 18 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <div>
            <label className="cs-section-title"><Car size={13} style={{ verticalAlign: -2, marginRight: 4 }} /> {t('Voertuig (optioneel)', 'Voertuig (optioneel)')}</label>
            <select className="cs-input" value={voertuig} data-testid="pv-voertuig-select"
              onChange={e => { setVoertuig(e.target.value); setInfo(null); }}>
              <option value="">— {t('Kies een voertuig', 'Kies een voertuig')} —</option>
              {klantAutos.map(a => (
                <option key={a.kenteken} value={a.kenteken}>{a.kenteken} · {[a.merk, a.model].filter(Boolean).join(' ')}</option>
              ))}
            </select>
            <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
              <input className="cs-input" placeholder={t('of typ kenteken', 'of typ kenteken')} value={kenteken}
                data-testid="pv-kenteken-input"
                onChange={e => setKenteken(e.target.value.toUpperCase())}
                onKeyDown={e => e.key === 'Enter' && doRdw()} />
              <button className="cs-btn" onClick={doRdw} disabled={rdwBusy} data-testid="pv-rdw-btn">
                {rdwBusy ? '…' : t('Zoek', 'Zoek')}
              </button>
            </div>
            {context && (
              <div style={{ marginTop: 8, fontSize: 12.5, color: '#16a34a', fontWeight: 600 }} data-testid="pv-voertuig-info">
                {[context.merk, context.model, context.bouwjaar].filter(Boolean).join(' · ')}
              </div>
            )}
          </div>
          <div>
            <label className="cs-section-title"><Search size={13} style={{ verticalAlign: -2, marginRight: 4 }} /> {t('Onderdeel', 'Onderdeel')}</label>
            <input className="cs-input" placeholder={t('bijv. remblokken voor', 'bijv. remblokken voor')} value={onderdeel}
              data-testid="pv-onderdeel-input"
              onChange={e => setOnderdeel(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && openAlle()} />
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
              {SNELKEUZE.map(s => (
                <button key={s} onClick={() => setOnderdeel(s)} data-testid={`pv-snelkeuze-${s}`}
                  style={{ padding: '4px 10px', borderRadius: 999, border: '1px solid #e2e8f0', background: onderdeel === s ? '#2563eb' : '#fff', color: onderdeel === s ? '#fff' : '#475569', fontSize: 12, cursor: 'pointer' }}>
                  {s}
                </button>
              ))}
            </div>
          </div>
        </div>

        {query && (
          <div style={{ marginTop: 12, fontSize: 12, color: '#64748b' }} data-testid="pv-query-preview">
            <Info size={12} style={{ verticalAlign: -2 }} /> {t('Zoekopdracht', 'Zoekopdracht')}: <strong>{query}</strong>
          </div>
        )}
      </div>

      {/* Shop-knoppen */}
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <h3 style={{ margin: 0, fontSize: 15, color: '#0f172a' }}>{t('Vergelijk in webshops', 'Vergelijk in webshops')}</h3>
          <button onClick={openAlle} data-testid="pv-open-alle"
            disabled={!onderdeel.trim()}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', border: 'none', borderRadius: 8, background: onderdeel.trim() ? '#0f172a' : '#cbd5e1', color: '#fff', fontWeight: 600, fontSize: 13, cursor: onderdeel.trim() ? 'pointer' : 'not-allowed' }}>
            <ExternalLink size={14} /> {t('Open alle 4', 'Open alle 4')}
          </button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
          {SHOPS.map(s => {
            const shopCoupons = coupons.filter(c => c.shop === s.id);
            const disabled = !onderdeel.trim();
            return (
              <div key={s.id} className="cs-card" data-testid={`pv-shop-${s.id}`} style={{ padding: 14, borderTop: `3px solid ${s.kleur}` }}>
                <div style={{ fontWeight: 700, color: '#0f172a', fontSize: 15 }}>{s.naam}</div>
                <div style={{ fontSize: 11.5, color: '#94a3b8', marginBottom: 10 }}>{s.domein}</div>
                <a href={disabled ? undefined : s.build(query)} target="_blank" rel="noopener noreferrer"
                  data-testid={`pv-shop-link-${s.id}`}
                  onClick={(e) => { if (disabled) { e.preventDefault(); toast.error(t('Typ eerst een onderdeel', 'Typ eerst een onderdeel')); } }}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 12px', borderRadius: 8, background: disabled ? '#f1f5f9' : s.kleur, color: disabled ? '#94a3b8' : '#fff', fontWeight: 600, fontSize: 13, textDecoration: 'none', cursor: disabled ? 'not-allowed' : 'pointer' }}>
                  <Search size={14} /> {t('Zoek bij', 'Zoek bij')} {s.naam}
                </a>
                {shopCoupons.length > 0 && (
                  <div style={{ marginTop: 10, display: 'grid', gap: 6 }}>
                    {shopCoupons.map(c => (
                      <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, padding: '5px 8px' }} data-testid={`pv-coupon-${c.id}`}>
                        <Tag size={12} color="#b45309" />
                        <code style={{ fontWeight: 700, fontSize: 12, color: '#92400e' }}>{c.code}</code>
                        {c.omschrijving && <span style={{ fontSize: 11, color: '#a16207', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.omschrijving}</span>}
                        <button onClick={() => copyCode(c.code)} title={t('Kopieer', 'Kopieer')} style={{ marginLeft: 'auto', border: 'none', background: 'transparent', cursor: 'pointer', color: '#b45309' }} data-testid={`pv-coupon-copy-${c.id}`}><Copy size={12} /></button>
                        <button onClick={() => delCoupon(c.id)} title={t('Verwijder', 'Verwijder')} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#dc2626' }} data-testid={`pv-coupon-del-${c.id}`}><Trash2 size={12} /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Kortingscodes toevoegen */}
      <div className="cs-card" data-testid="pv-coupon-beheer" style={{ padding: 16 }}>
        <h3 style={{ margin: '0 0 4px', fontSize: 15, color: '#0f172a' }}><Tag size={15} style={{ verticalAlign: -2, marginRight: 6 }} />{t('Eigen kortingscodes', 'Eigen kortingscodes')}</h3>
        <p style={{ margin: '0 0 12px', fontSize: 12.5, color: '#64748b' }}>
          {t('Bewaar hier je eigen kortingscodes per shop. Ze verschijnen dan bij de knop hierboven.', 'Bewaar hier je eigen kortingscodes per shop. Ze verschijnen dan bij de knop hierboven.')}
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
          <select className="cs-input" style={{ maxWidth: 200 }} value={nieuw.shop} data-testid="pv-coupon-shop"
            onChange={e => setNieuw(n => ({ ...n, shop: e.target.value }))}>
            {SHOPS.map(s => <option key={s.id} value={s.id}>{s.naam}</option>)}
          </select>
          <input className="cs-input" style={{ maxWidth: 160 }} placeholder={t('Code', 'Code')} value={nieuw.code}
            data-testid="pv-coupon-code" onChange={e => setNieuw(n => ({ ...n, code: e.target.value }))} />
          <input className="cs-input" style={{ flex: 1, minWidth: 180 }} placeholder={t('Omschrijving (bijv. 10% korting)', 'Omschrijving (bijv. 10% korting)')} value={nieuw.omschrijving}
            data-testid="pv-coupon-omschrijving" onChange={e => setNieuw(n => ({ ...n, omschrijving: e.target.value }))} />
          <button className="cs-btn primary" onClick={addCoupon} data-testid="pv-coupon-add"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><Plus size={14} /> {t('Toevoegen', 'Toevoegen')}</button>
        </div>
        {coupons.length === 0 && (
          <div style={{ marginTop: 12, fontSize: 12.5, color: '#94a3b8' }}>{t('Nog geen kortingscodes opgeslagen.', 'Nog geen kortingscodes opgeslagen.')}</div>
        )}
      </div>

      <div style={{ fontSize: 11.5, color: '#94a3b8', display: 'flex', alignItems: 'flex-start', gap: 6 }}>
        <Info size={13} style={{ flexShrink: 0, marginTop: 1 }} />
        <span>{t('Tip: kies een voertuig zodat merk/model/bouwjaar wordt meegezocht. Prijzen worden live in de webshop getoond (openen in een nieuw tabblad).', 'Tip: kies een voertuig zodat merk/model/bouwjaar wordt meegezocht. Prijzen worden live in de webshop getoond (openen in een nieuw tabblad).')}</span>
      </div>
    </div>
  );
}
